// src/pages/UserSignUp.jsx
// Wayroot — User signup + OTP verification + profile completion (enterprise style, matches SignIn)
// ✅ Single clean card (no pitch/summary panels)
// ✅ Only required: phone + OTP + userName
// ✅ Optional: email, location (autocomplete) + "Use current location"
// ✅ If coming after OTP verification (via=signin): phone is locked, OTP step is skipped
// ✅ Logic intact: request OTP → verify OTP → if new user show profile completion → complete → redirect
// Note: this uses UserContext methods like SignIn page: userRequestOtp + userSignInOtp (+ userCompleteProfile if available)

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useUser } from "../context/UserContext";
import API_BASE_URL from "../api/baseUrl";

function cleanPhone(p = "") {
  return String(p).replace(/\D/g, "").slice(-10);
}

function maskPhone10(p = "") {
  const s = cleanPhone(p);
  if (s.length !== 10) return s;
  return `+91 ${s.slice(0, 2)}••• •••${s.slice(7)}`;
}

export default function UserSignUp() {
  const nav = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  const redirect = params.get("redirect") || "/";
  const via = params.get("via") || ""; // e.g. "signin"
  const seededPhone = cleanPhone(params.get("phoneNumber") || "");

  // Context
  const { userRequestOtp, userSignInOtp, userCompleteProfile } = useUser();

  // Step handling:
  // - Normal register: phone -> otp -> profile
  // - via=signin: already verified on signin page and isNewUser, so go straight to profile (phone locked)
  const [step, setStep] = useState(
    via === "signin" && seededPhone.length === 10 ? "profile" : "phone"
  ); // "phone" | "otp" | "profile"

  // Core fields
  const [phoneNumber, setPhoneNumber] = useState(seededPhone || "");
  const normalizedPhone = useMemo(() => cleanPhone(phoneNumber), [phoneNumber]);

  const [otp, setOtp] = useState("");
  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [userLocation, setUserLocation] = useState("");
  const [latitude, setLatitude] = useState(undefined);
  const [longitude, setLongitude] = useState(undefined);

  // Location suggestions
  const [userLocationSuggestions, setUserLocationSuggestions] = useState([]);

  // UX state
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Resend cooldown
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = window.setInterval(() => {
      setCooldown((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [cooldown]);

  const startCooldown = (seconds = 30) => setCooldown(seconds);

  const canSendOtp = normalizedPhone.length === 10 && !busy && cooldown === 0;
  const canVerifyOtp =
    step === "otp" && normalizedPhone.length === 10 && otp.trim().length >= 4 && !busy;
  const canCompleteProfile = step === "profile" && userName.trim().length >= 2 && !busy;

  // If user edits location text, fetch suggestions
  const fetchLocationSuggestions = async (q) => {
    const input = String(q || "").trim();
    if (!input) {
      setUserLocationSuggestions([]);
      return;
    }
    try {
      // Keep your existing endpoint style (but use API_BASE_URL)
      const resp = await fetch(
        `${API_BASE_URL}/api/vehicles/autocomplete/location-predictions/${encodeURIComponent(input)}`
      );
      const data = await resp.json();
      setUserLocationSuggestions(Array.isArray(data) ? data : Array.isArray(data?.predictions) ? data.predictions : []);
    } catch {
      setUserLocationSuggestions([]);
      setError("Error fetching location suggestions. Please try again.");
    }
  };

  const handleFetchCurrentLocation = async () => {
    setError("");
    try {
      const resp = await fetch(`${API_BASE_URL}/api/users/current-location`);
      const data = await resp.json(); // { latitude, longitude, humanReadable }
      setUserLocation(data?.humanReadable || "");
      setLatitude(data?.latitude);
      setLongitude(data?.longitude);
      setUserLocationSuggestions([]);
    } catch {
      setError("Error fetching current location. Please try again later.");
    }
  };

  const pickSuggestion = (s) => {
    const label = s?.fullText || s?.description || "";
    setUserLocation(label);
    setUserLocationSuggestions([]);
  };

  const resetOtpFlow = () => {
    setOtp("");
    setError("");
    setStep("phone");
  };

  const handleSendOtp = async () => {
    setError("");
    if (normalizedPhone.length !== 10) {
      setError("Enter a valid 10-digit mobile number.");
      return;
    }
    try {
      setBusy(true);
      await userRequestOtp(normalizedPhone);
      setStep("otp");
      setOtp("");
      startCooldown(30);
    } catch {
      setError("Could not send OTP. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");

    if (normalizedPhone.length !== 10) {
      setError("Phone number looks invalid. Please re-enter.");
      resetOtpFlow();
      return;
    }
    if (otp.trim().length < 4) {
      setError("Enter OTP and try again.");
      return;
    }

    try {
      setBusy(true);
      const res = await userSignInOtp({ phoneNumber: normalizedPhone, otp: otp.trim() });

      // If backend says this is a new user, show profile completion on this page
      if (res?.isNewUser) {
        setStep("profile");
        return;
      }

      // Existing user: redirect immediately
      nav(redirect, { replace: true });
    } catch {
      setError("Invalid or expired OTP. Please request a new OTP.");
    } finally {
      setBusy(false);
    }
  };

  const handleCompleteProfile = async (e) => {
    e.preventDefault();
    setError("");

    if (userName.trim().length < 2) {
      setError("Please enter your name.");
      return;
    }

    // Keep payload minimal & aligned with your new approach
    const payload = {
      userName: userName.trim(),
      phoneNumber: normalizedPhone, // handy if backend uses it; safe even if ignored
      email: email.trim() ? email.trim() : null,
      userLocation: userLocation.trim() ? userLocation.trim() : null,
      currentLocation: userLocation.trim() ? userLocation.trim() : null,
      latitude: typeof latitude === "number" ? latitude : undefined,
      longitude: typeof longitude === "number" ? longitude : undefined,
    };

    try {
      setBusy(true);

      // Preferred: context completes profile using JWT from OTP verify
      if (typeof userCompleteProfile === "function") {
        await userCompleteProfile(payload);
      } else {
        // Fallback if your context doesn’t yet expose it.
        // Use API_BASE_URL so prod hits api.wayroot.com
        await fetch(`${API_BASE_URL}/api/users/auth/profile/complete`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          },
          body: JSON.stringify(payload),
        });
      }

      nav(redirect, { replace: true });
    } catch {
      setError("Could not complete profile. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const showTopHelp =
    step === "phone"
      ? "Verify your phone number to create your account."
      : step === "otp"
      ? `Enter the OTP sent to ${maskPhone10(normalizedPhone)}.`
      : "Complete your profile to finish setup.";

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Header />

      <main className="mx-auto w-full max-w-[560px] px-4 sm:px-6 xl:px-8 py-10 sm:py-14">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="h-1 w-full bg-gradient-to-r from-black via-[#FF2D83] to-black" />

          <div className="p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold tracking-tight">
                  {step === "profile" ? "Complete your profile" : "Create account"}
                </h1>
                <p className="mt-1 text-sm text-slate-600">{showTopHelp}</p>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <StepDot active />
                <div
                  className={[
                    "h-[2px] w-10",
                    step === "otp" || step === "profile" ? "bg-slate-900" : "bg-slate-200",
                  ].join(" ")}
                />
                <StepDot active={step === "otp" || step === "profile"} />
                <div
                  className={[
                    "h-[2px] w-10",
                    step === "profile" ? "bg-slate-900" : "bg-slate-200",
                  ].join(" ")}
                />
                <StepDot active={step === "profile"} />
              </div>
            </div>

            {error && (
              <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            {/* STEP 1 — Phone */}
            {step === "phone" && (
              <div className="mt-6 space-y-5">
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Mobile number</span>

                  <div className="mt-2 flex items-center gap-3 rounded-2xl border border-slate-300 bg-white px-3.5 py-2.5 focus-within:ring-2 focus-within:ring-slate-900/10">
                    <span className="text-sm font-semibold text-slate-700 select-none">+91</span>
                    <div className="h-5 w-px bg-slate-200" />
                    <input
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      placeholder="10-digit number"
                      value={phoneNumber}
                      onChange={(e) => {
                        setPhoneNumber(cleanPhone(e.target.value));
                        setError("");
                      }}
                      className="w-full bg-transparent text-[15px] outline-none placeholder:text-slate-400"
                    />
                  </div>

                  <div className="mt-2 text-xs text-slate-500">
                    We’ll send a one-time password to this number.
                  </div>
                </label>

                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={!canSendOtp}
                  className="w-full inline-flex items-center justify-center rounded-2xl bg-black px-5 py-3 text-white font-medium hover:opacity-90 active:opacity-80 disabled:opacity-60"
                >
                  {busy ? "Sending OTP..." : "Send OTP"}
                </button>

                <div className="pt-2 text-sm text-slate-700">
                  Already have an account?{" "}
                  <Link
                    to={`/login?redirect=${encodeURIComponent(redirect)}`}
                    className="font-semibold underline underline-offset-4 hover:no-underline"
                  >
                    Sign in
                  </Link>
                </div>
              </div>
            )}

            {/* STEP 2 — OTP */}
            {step === "otp" && (
              <form onSubmit={handleVerifyOtp} className="mt-6 space-y-5">
                <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div>
                    <div className="text-[11px] font-semibold tracking-[0.18em] text-slate-500">
                      MOBILE
                    </div>
                    <div className="mt-0.5 text-sm font-semibold text-slate-900">
                      {maskPhone10(normalizedPhone)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setError("");
                      setOtp("");
                      setStep("phone");
                    }}
                    className="text-xs font-semibold text-[#FF2D83] hover:opacity-80"
                  >
                    Change
                  </button>
                </div>

                <label className="block">
                  <span className="text-sm font-medium text-slate-700">One-time password</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="4–6 digit OTP"
                    value={otp}
                    onChange={(e) => {
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                      setError("");
                    }}
                    className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-[16px] tracking-[0.22em] text-slate-900 outline-none focus:ring-2 focus:ring-slate-900/10 placeholder:tracking-normal placeholder:text-slate-400"
                  />

                  <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                    <span>Didn’t receive it?</span>
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={busy || normalizedPhone.length !== 10 || cooldown > 0}
                      className="font-semibold text-[#FF2D83] disabled:text-slate-300"
                    >
                      {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
                    </button>
                  </div>
                </label>

                <button
                  type="submit"
                  disabled={!canVerifyOtp}
                  className="w-full inline-flex items-center justify-center rounded-2xl bg-black px-5 py-3 text-white font-medium hover:opacity-90 active:opacity-80 disabled:opacity-60"
                >
                  {busy ? "Verifying..." : "Verify & continue"}
                </button>
              </form>
            )}

            {/* STEP 3 — Profile completion */}
            {step === "profile" && (
              <form onSubmit={handleCompleteProfile} className="mt-6 space-y-5">
                <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div>
                    <div className="text-[11px] font-semibold tracking-[0.18em] text-slate-500">
                      MOBILE
                    </div>
                    <div className="mt-0.5 text-sm font-semibold text-slate-900">
                      {maskPhone10(normalizedPhone || seededPhone)}
                    </div>
                  </div>

                  {/* Only allow changing phone when not via=signin */}
                  {!(via === "signin" && seededPhone.length === 10) && (
                    <button
                      type="button"
                      onClick={() => {
                        setError("");
                        setOtp("");
                        setStep("phone");
                      }}
                      className="text-xs font-semibold text-[#FF2D83] hover:opacity-80"
                    >
                      Change
                    </button>
                  )}
                </div>

                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Full name</span>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={userName}
                    onChange={(e) => {
                      setUserName(e.target.value);
                      setError("");
                    }}
                    className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-[15px] outline-none focus:ring-2 focus:ring-slate-900/10 placeholder:text-slate-400"
                  />
                  <div className="mt-2 text-xs text-slate-500">
                    This will appear on your bookings and receipts.
                  </div>
                </label>

                <label className="block">
                  <span className="text-sm font-medium text-slate-700">
                    Email <span className="text-slate-400 font-normal">(optional)</span>
                  </span>
                  <input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError("");
                    }}
                    className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-[15px] outline-none focus:ring-2 focus:ring-slate-900/10 placeholder:text-slate-400"
                  />
                </label>

                <div className="block">
                  <span className="text-sm font-medium text-slate-700">
                    Location <span className="text-slate-400 font-normal">(optional)</span>
                  </span>

                  <div className="mt-2 flex gap-2">
                    <input
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-[15px] outline-none focus:ring-2 focus:ring-slate-900/10 placeholder:text-slate-400"
                      type="text"
                      name="userLocation"
                      placeholder="Search location"
                      value={userLocation}
                      onChange={(e) => {
                        const v = e.target.value;
                        setUserLocation(v);
                        setError("");
                        fetchLocationSuggestions(v);
                      }}
                      autoComplete="off"
                    />

                    <button
                      className="shrink-0 rounded-2xl border border-slate-300 px-4 py-3 text-sm font-semibold hover:bg-slate-50"
                      type="button"
                      onClick={handleFetchCurrentLocation}
                      disabled={busy}
                    >
                      Use current
                    </button>
                  </div>

                  {userLocationSuggestions.length > 0 && (
                    <ul className="mt-2 max-h-56 overflow-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
                      {userLocationSuggestions.map((s, i) => (
                        <li
                          key={`${s.place_id || i}`}
                          className="px-4 py-2.5 text-sm text-slate-800 hover:bg-slate-50 cursor-pointer"
                          onClick={() => pickSuggestion(s)}
                        >
                          <div className="font-medium text-slate-800">{s.primaryText || s.fullText || s.description}</div>
                          {s.secondaryText ? <div className="mt-0.5 text-xs text-slate-500">{s.secondaryText}</div> : null}
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-2 text-xs text-slate-500">
                    Helps improve search results and booking suggestions.
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={!canCompleteProfile}
                  className="w-full inline-flex items-center justify-center rounded-2xl bg-black px-5 py-3 text-white font-medium hover:opacity-90 active:opacity-80 disabled:opacity-60"
                >
                  {busy ? "Saving..." : "Save & continue"}
                </button>

                <div className="text-sm text-slate-700">
                  Already have an account?{" "}
                  <Link
                    to={`/login?redirect=${encodeURIComponent(redirect)}`}
                    className="font-semibold underline underline-offset-4 hover:no-underline"
                  >
                    Sign in
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function StepDot({ active = false }) {
  return (
    <div
      className={[
        "h-2.5 w-2.5 rounded-full border",
        active ? "bg-slate-900 border-slate-900" : "bg-white border-slate-300",
      ].join(" ")}
    />
  );
}